<template>
    <v-card class="app-screen-bg pl-2 pl-md-10 dashboard-page">
        <div class="dashboard-page__scroll">
            <div class="dashboard-page__sticky-header">
                <div class="dashboard-toolbar" :class="$vuetify.theme.dark ? 'transparent07--dark' : 'transparent07'">
                <v-menu
                    v-model="filterOpen"
                    :close-on-content-click="false"
                    offset-y
                >
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn icon v-bind="attrs" v-on="on">
                            <v-icon size="26" :color="activeFilterCount ? 'primary' : undefined">mdi-filter-variant</v-icon>
                        </v-btn>
                    </template>
                    <v-card class="dashboard-filter-popup">
                        <v-card-text>
                            <v-select
                                v-model="order"
                                item-text="name"
                                item-value="value"
                                color="accent"
                                item-color="accent"
                                :label="$t('dashboard.trainerList.orderLabel')"
                                :loading="isLoading"
                                :items="orders"
                                hide-details
                                dense
                                class="mb-4"
                            />
                            <v-switch
                                v-model="asc"
                                flat
                                color="accent"
                                hide-details
                                class="mb-3"
                                :label="asc ? $t('dashboard.trainerList.ascLabel') : $t('dashboard.trainerList.descLabel')"
                            />
                            <div class="dashboard-category-list">
                                <v-checkbox
                                    v-for="category in categoryOptions"
                                    :key="category.value"
                                    v-model="categories"
                                    :value="category.value"
                                    :label="category.name"
                                    color="accent"
                                    hide-details
                                    dense
                                    class="ma-0"
                                />
                            </div>
                        </v-card-text>
                    </v-card>
                </v-menu>
                <span v-if="activeFilterCount" class="dashboard-filter-hint">{{ $tc('dashboard.trainerList.filterHint', activeFilterCount, {n: activeFilterCount}) }}</span>

                <v-spacer/>

                <v-pagination
                    v-model="page"
                    color="accent"
                    :total-visible="isMobile ? 5 : 7"
                    prev-icon="mdi-menu-left"
                    next-icon="mdi-menu-right"
                    class="dashboard-pagination"
                ></v-pagination>

                <v-select
                    v-model="itemsPerPage"
                    color="accent"
                    item-color="accent"
                    :loading="isLoading"
                    :label="$t('dashboard.trainerList.perPageLabel')"
                    hide-details
                    dense
                    :items="[5,10,30,50,1000]"
                    class="dashboard-per-page"
                />
                </div>

                <div class="dashboard-title-pill-wrap">
                    <span class="dashboard-title-pill">{{ $t('dashboard.trainerList.title') }}</span>
                </div>
            </div>

            <div class="dashboard-grid" v-if="trainers.length > 0">
                <TrainerCard
                    v-for="trainer in trainers"
                    :key="trainer.name"
                    :trainer="trainer"
                    @click="openDetail(trainer)"
                />
            </div>
        </div>

        <TrainerDetailPopup v-model="detailOpen" :trainer="selectedTrainer"/>
    </v-card>
</template>

<script>
    import {mapActions, mapGetters} from 'vuex';
    import TrainerCard from "../trainer/TrainerCard";
    import TrainerDetailPopup from "../trainer/TrainerDetailPopup";
    import {isMobileMixin} from '@/utils/isMobileMixin';

    export default {
        components: {TrainerCard, TrainerDetailPopup},
        mixins: [isMobileMixin],
        data() {
            return {
                page: 1,
                // Prazdne pole = bez filtru (vsichni); kategorie se kombinuji pres OR (TrainerCategoryFilter.php na BE)
                categories: [],
                itemsPerPage: 30,
                totalItems: 1,
                maxPage: 1,
                asc: false,
                // Vychozi razeni - nejnovejsi (nejvyssi id) prvni
                order: 'id',
                filterOpen: false,
                detailOpen: false,
                selectedTrainer: null,
            }
        },
        computed: {
            ...mapGetters('dashboard', [
              'isLoading',
              'user',
            ]),
            ...mapGetters({
              trainers: 'dashboard/items'
            }),
            categoryOptions() {
                return [
                    {value: 'player', name: this.$t('dashboard.trainerList.categories.player')},
                    {value: 'regular', name: this.$t('dashboard.trainerList.categories.regular')},
                    {value: 'league', name: this.$t('dashboard.trainerList.categories.league')},
                    {value: 'rocket', name: this.$t('dashboard.trainerList.categories.rocket')},
                    {value: 'stadium', name: this.$t('dashboard.trainerList.categories.stadium')},
                ];
            },
            // Musi byt computed, ne data() - obsahuje $t() volani (viz i18n-conventions.md, bod 4).
            orders() {
                return [
                    {value: 'id', name: this.$t('dashboard.trainerList.orderNewest')},
                    {value: null, name: '---'},
                    {value: 'experience.level', name: this.$t('dashboard.trainerList.orderLvl')},
                    {value: 'name', name: this.$t('dashboard.trainerList.orderName')},
                    {value: 'position.days', name: this.$t('dashboard.trainerList.orderDay')},
                ];
            },
            activeFilterCount() {
                // "id" (Newest) je vychozi razeni, ne aktivni filtr - nepocitat
                return (this.order !== 'id' ? 1 : 0)
                    + (this.asc !== false ? 1 : 0)
                    + (this.categories.length > 0 ? 1 : 0);
            },
        },
        watch: {
            itemsPerPage() {
                this.recalculateMaxPage();
                this.refreshTrainers();
            },
            page() {
                this.recalculateMaxPage();
                this.refreshTrainers();
            },
            asc() {
                this.recalculateMaxPage();
                this.refreshTrainers();
            },
            order() {
                this.recalculateMaxPage();
                this.refreshTrainers();
            },
            categories() {
                this.recalculateMaxPage();
                this.refreshTrainers();
            },
        },
        created() {
            this.refreshTrainers();
        },
        methods: {
            ...mapActions({
                getTrainers: 'dashboard/loadOverview',
                getUser: 'dashboard/loadUser',
            }),
            recalculateMaxPage() {
                let maxPage = Math.ceil(this.totalItems/this.itemsPerPage);
                if (this.page > maxPage) {
                    this.page = maxPage;
                }
                this.maxPage = maxPage;
            },
            refreshTrainers() {
                this.getTrainers({page: this.page, itemsPerPage: this.itemsPerPage, categories: this.categories, order: this.order, asc: this.asc}).then(
                    (data) =>  {
                        this.totalItems = data;
                        this.recalculateMaxPage();
                    }
                );
            },
            openDetail(trainer) {
                this.selectedTrainer = trainer;
                this.detailOpen = true;
            },
        },
    }
</script>

<style scoped>
    .dashboard-page {
        /* ne 100vh - viz komentar u ".new-shell-ui .v-main" v App.vue */
        height: 100%;
        display: flex;
        flex-direction: column;
    }

    .dashboard-page__scroll {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        padding-right: 8px;
    }

    /* "position: sticky" primo uvnitr scrollovaci oblasti, ne flex sourozenec mimo ni - viz claude/architecture/frontend.md bod 8 */
    .dashboard-page__sticky-header {
        position: sticky;
        top: 0;
        z-index: 3;
        padding-bottom: 4px;
    }

    .dashboard-title-pill-wrap {
        display: flex;
        justify-content: center;
        padding: 8px 0;
    }

    .dashboard-title-pill {
        display: inline-block;
        background: rgba(0, 0, 0, .65);
        color: #fff;
        font-size: 20px;
        font-weight: 700;
        padding: 6px 20px;
        border-radius: 20px;
        text-shadow: 1px 1px 2px rgba(0, 0, 0, .6);
    }

    .dashboard-toolbar {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
        padding: 6px 8px;
        border-radius: 12px;
    }

    .dashboard-filter-popup {
        width: 260px;
    }

    .dashboard-category-list {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .dashboard-filter-hint {
        font-size: .8rem;
    }

    .theme--light .dashboard-filter-hint {
        color: rgba(0, 0, 0, .6);
    }
    .theme--dark .dashboard-filter-hint {
        color: rgba(255, 255, 255, .7);
    }

    .dashboard-pagination {
        flex: 0 1 auto;
    }

    .dashboard-per-page {
        flex: 0 0 100px;
    }

    .dashboard-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(min(140px, calc(50% - 8px)), 1fr));
        gap: 12px;
        align-items: stretch;
        padding: 4px 4px 16px;
    }
</style>
